import {ZadAuditUser, ZadSale, ZadSalesValidationAudit, ZadSalesValidationMe, ZadSalesValidationPermissionUser} from '../../../models/zad'
import {formatZadUser} from './stockHelpers'

export const ZAD_SALES_VALIDATE_KEY = 'ZAD_SALES_VALIDATE'
export const SALE_VALIDATION_FORBIDDEN = 'SALE_VALIDATION_FORBIDDEN'

function asId(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object' && value && '_id' in value) {
    return String((value as {_id?: unknown})._id || '')
  }
  return String(value)
}

export function canManageZadSalesValidationPermissions(role?: string | number | null): boolean {
  return String(role ?? '') === '2'
}

export function extractZadSalesValidationMe(data: any): ZadSalesValidationMe | null {
  if (!data || data.success === false) return null
  const enabled = data.enabled === true
  return {
    permissionKey: data.permissionKey || ZAD_SALES_VALIDATE_KEY,
    enabled,
    role: data.role != null ? String(data.role) : null,
    alwaysAllowed: data.alwaysAllowed === true,
  }
}

export function extractZadSalesValidationUsers(data: any): ZadSalesValidationPermissionUser[] {
  const rows = Array.isArray(data?.users) ? data.users : []
  return rows.map((row: any) => ({
    userId: asId(row.userId),
    displayName: row.displayName || null,
    email: row.email || null,
    role: row.role != null ? String(row.role) : '11',
    enabled: row.enabled === true,
    source: row.source === 'EXPLICIT' ? 'EXPLICIT' : 'DEFAULT',
    isAccountActivated: row.isAccountActivated !== false,
  }))
}

export function extractZadSalesValidationAudits(data: any): ZadSalesValidationAudit[] {
  const rows = Array.isArray(data?.audits) ? data.audits : []
  return rows.map((row: any) => ({
    _id: asId(row._id) || undefined,
    targetUser: row.targetUser || null,
    permissionKey: row.permissionKey || ZAD_SALES_VALIDATE_KEY,
    previousValue: row.previousValue === true ? true : row.previousValue === false ? false : null,
    newValue: row.newValue === true,
    action: row.action || '',
    changedBy: row.changedBy || null,
    changedAt: row.changedAt || null,
  }))
}

export function isZadSalesValidationAllowed(
  me: ZadSalesValidationMe | null,
  authRole?: string | number | null,
  options?: {loading?: boolean; failed?: boolean}
): boolean {
  if (options?.loading || options?.failed) return false
  if (!me) return false
  if (me.alwaysAllowed === true) return true
  const role = String(me.role ?? authRole ?? '')
  if (role === '2') return true
  if (role === '6') return false
  if (role === '11') return me.enabled === true
  return false
}

export function isZadSaleValidationForbidden(err: {errorCode?: string | null; status?: number | null}): boolean {
  return err.errorCode === SALE_VALIDATION_FORBIDDEN || (err.status === 403 && err.errorCode === SALE_VALIDATION_FORBIDDEN)
}

export function formatZadPermissionPerson(user: ZadAuditUser | string | null | undefined, fallbackName?: string | null): string {
  const frozen = (fallbackName || '').trim()
  if (frozen) return frozen
  return formatZadUser(user)
}

export function formatZadSaleCreatedBy(sale: Pick<ZadSale, 'createdByName' | 'createdBy'>): string {
  const frozen = (sale.createdByName || '').trim()
  if (frozen) return frozen
  const fromUser = formatZadUser(sale.createdBy)
  return fromUser === '—' ? '' : fromUser
}

export function formatZadSaleValidatedBy(
  sale: Pick<ZadSale, 'status' | 'validatedByName' | 'validatedBy' | 'validatedAt'>,
  missingLabel: string
): string {
  if (sale.status !== 'VALIDATED') return '—'
  const frozen = (sale.validatedByName || '').trim()
  if (frozen) return frozen
  const fromUser = formatZadUser(sale.validatedBy)
  if (fromUser !== '—') return fromUser
  return missingLabel
}

export function formatZadSaleValidatedAt(
  sale: Pick<ZadSale, 'status' | 'validatedAt'>,
  formatted: string
): string {
  if (sale.status !== 'VALIDATED') return '—'
  return formatted || '—'
}

export function formatZadPermissionPreviousValue(
  previousValue: boolean | null | undefined,
  defaultLabel: string,
  enabledLabel: string,
  disabledLabel: string
): string {
  if (previousValue === true) return enabledLabel
  if (previousValue === false) return disabledLabel
  return defaultLabel
}

export function formatZadPermissionAction(action?: string | null): string {
  return String(action || '').trim()
}
